import React from 'react';
import { useFormContext } from 'react-hook-form';

import CustomSwitchButton from 'components/base/CustomSwitchButton';
import CustomTitleWithInput from 'components/base/CustomTitleWithInput';
import { CustomTitleLabel } from 'components/earning/CustomBaseComponments';

import { DISPLAY_LEVEL_RENEWAL_LOGIC, LEVEL_CALCULATION_SOURCE, LEVEL_RENEWAL_LOGIC } from 'config/CustomEnums';
import { SectionTopTitle } from 'components/base/CommonComponent';
import { LevelErrorHandleFields } from 'containers/engagement/level/LevelErrorHandle';
import ErrorFieldMessage, { hasError } from 'components/base/ErrorFieldMessage';
import ReactHookFormNumberMutipleInputWithUnit from 'components/reactHookFormComponents/ReactHookFormNumberMutipleInputWithUnit';
import './EntryRequirementSection.scss';

const EntryRequirementSection = ({
}) => {
  const {
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();

  const levelCalculationSourceField = "levelCalculationSource";
  const isLowestLevelField = "isLowestLevel";
  const levelRankField = "levelRank";
  const enableCheckTargetTotalPointsEarnedField = "enableCheckTargetTotalPointsEarned";
  const targetTotalPointsEarnedField = "targetTotalPointsEarned";
  const enableCheckAccumulatedSpendingInAPeriodField = "enableCheckAccumulatedSpendingInAPeriod";
  const accumulatedSpendingInThePeriodField = "accumulatedSpendingInThePeriod";
  const accumulatedSpendingInXDayField = "accumulatedSpendingInXDay";
  const enableCheckSpendingInOnePurchaseField = "enableCheckSpendingInOnePurchase";
  const spendingInOnePurchaseField = "spendingInOnePurchase";

  const watchLevelCalculationSource = watch(levelCalculationSourceField);
  const watchIsLowestLevel = watch(isLowestLevelField);
  const watchLevelRank = watch(levelRankField);
  const watchEnableCheckTargetTotalPointsEarned = watch(enableCheckTargetTotalPointsEarnedField);
  const watchTargetTotalPointsEarned = watch(targetTotalPointsEarnedField);
  const watchEnableCheckAccumulatedSpendingInAPeriod = watch(enableCheckAccumulatedSpendingInAPeriodField);
  const watchAccumulatedSpendingInThePeriod = watch(accumulatedSpendingInThePeriodField);
  const watchAccumulatedSpendingInXDay = watch(accumulatedSpendingInXDayField);
  const watchEnableCheckSpendingInOnePurchase = watch(enableCheckSpendingInOnePurchaseField);
  const watchSpendingInOnePurchase = watch(spendingInOnePurchaseField);

  const isCalculatePP = watchLevelCalculationSource === LEVEL_CALCULATION_SOURCE.pp;

  return (
    <>
      <SectionTopTitle title="ENTRY REQUIREMENT" />

      <CustomTitleLabel title="Are you setting the lowest level (level 1)?" />
      <div className="second-section_description">
        If toggle on, no further setting will be required.
      </div>
      <div className='entry-requirement-div'>
        <CustomSwitchButton
          defaultChecked={watchIsLowestLevel}
          onChange={(isChecked) => {
            setValue(isLowestLevelField, isChecked, { shouldDirty: true });
            setValue(levelRankField, 1, { shouldDirty: true });
            setValue(enableCheckTargetTotalPointsEarnedField, true, { shouldDirty: true });
            setValue(targetTotalPointsEarnedField, 0, { shouldDirty: true });
            setValue(enableCheckAccumulatedSpendingInAPeriodField, false, { shouldDirty: true });
            setValue(accumulatedSpendingInThePeriodField, '', { shouldDirty: true });
            setValue(accumulatedSpendingInXDayField, '', { shouldDirty: true });
            setValue(enableCheckSpendingInOnePurchaseField, false, { shouldDirty: true });
            setValue(spendingInOnePurchaseField, '', { shouldDirty: true });
            setValue('displayRenewalLogic', DISPLAY_LEVEL_RENEWAL_LOGIC[LEVEL_RENEWAL_LOGIC.retainCurrentLevel], { shouldDirty: true });
          }}
        />
      </div>
      <ErrorFieldMessage
        id={isLowestLevelField}
        error={hasError(errors, isLowestLevelField)}
        message={LevelErrorHandleFields.levelExists}
      />

      {
        !watchIsLowestLevel && (
          <CustomTitleWithInput
            type="number"
            title="Which level are you setting? (Input 2 or above)"
            shortDescription="Customers can get upgraded when either one of the following conditions is met."
            customClass="level-rank-input"
            defaultValue={watchLevelRank}
            setValue={(value) => {
              setValue(levelRankField, value, { shouldDirty: true, });
            }}
            error={{
              id: levelRankField,
              error: hasError(errors, levelRankField),
              message: hasError(errors, levelRankField),
            }}
          />
        )
      }

      {/* condition 1 */}
      <div className="content-area">
        <CustomTitleLabel title={
          `Condition 1: Accumulated ${isCalculatePP ? "PP" : "spending"} within current membership period (since last renewal if any)`
        } />
        <div className='entry-requirement-div'>
          <CustomSwitchButton
            disabled={watchIsLowestLevel}
            defaultChecked={watchEnableCheckTargetTotalPointsEarned}
            onChange={(isChecked) => {
              setValue(enableCheckTargetTotalPointsEarnedField, isChecked, { shouldDirty: true });
            }}
          />
        </div>
        <ErrorFieldMessage
          id={enableCheckTargetTotalPointsEarnedField}
          error={hasError(errors, enableCheckTargetTotalPointsEarnedField)}
          message={hasError(errors, enableCheckTargetTotalPointsEarnedField)}
        />
        <CustomTitleWithInput
          type="number"
          customClass="level-common-input"
          unit={isCalculatePP ? "PP" : "HK$"}
          disabled={watchIsLowestLevel || !watchEnableCheckTargetTotalPointsEarned}
          useDefaultValue={!watchIsLowestLevel}
          defaultValue={watchTargetTotalPointsEarned}
          setValue={(value) => {
            setValue(targetTotalPointsEarnedField, value, { shouldDirty: true, });
          }}
          error={{
            id: targetTotalPointsEarnedField,
            error: hasError(errors, targetTotalPointsEarnedField),
            message: hasError(errors, targetTotalPointsEarnedField),
          }}
        />
      </div>

      {/* condition 2 */}
      <div className="content-area">
        <CustomTitleLabel title="Condition 2: Accumulated spending in a period" />
        <div className='entry-requirement-div'>
          <CustomSwitchButton
            disabled={watchIsLowestLevel}
            defaultChecked={watchEnableCheckAccumulatedSpendingInAPeriod}
            onChange={(isChecked) => {
              setValue(enableCheckAccumulatedSpendingInAPeriodField, isChecked, { shouldDirty: true });
            }}
          />
        </div>
        <ReactHookFormNumberMutipleInputWithUnit
          disabled={watchIsLowestLevel || !watchEnableCheckAccumulatedSpendingInAPeriod}
          useDefaultValue={!watchIsLowestLevel}
          per="HK$ in"
          unit="days"
          firstValueFieldName="accumulatedSpendingInThePeriod"
          secondValueFieldName="accumulatedSpendingInXDay"
          errorId="accumulatedSpendingInThePeriod"
        />
      </div>


      {/* condition 3 */}
      <div className="content-area">
        <CustomTitleLabel title="Condition 3: Accumulated spending in one single purchase" />
        <div className='entry-requirement-div'>
          <CustomSwitchButton
            disabled={watchIsLowestLevel}
            defaultChecked={watchEnableCheckSpendingInOnePurchase}
            onChange={(isChecked) => {
              setValue(enableCheckSpendingInOnePurchaseField, isChecked, { shouldDirty: true });
            }}
          />
        </div>
        <CustomTitleWithInput
          customClass="level-common-input"
          unit="HK$ in one single purchase"
          disabled={watchIsLowestLevel || !watchEnableCheckSpendingInOnePurchase}
          useDefaultValue={!watchIsLowestLevel}
          defaultValue={watchSpendingInOnePurchase}
          setValue={(value) => {
            setValue(spendingInOnePurchaseField, value, { shouldDirty: true, });
          }}
          error={{
            id: spendingInOnePurchaseField,
            error: hasError(errors, spendingInOnePurchaseField),
            message: hasError(errors, spendingInOnePurchaseField),
          }}
        />
      </div>
    </>
  );
}

export default EntryRequirementSection;
